import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { reportVslClarity } from './vsl-clarity';

/** Same Clarity project GTM runs on /vsl. */
export const CLARITY_PROJECT_ID = 'ytpod4rysx';

export type FunnelStep = 'vsl' | 'book' | 'filled';

export function funnelStep(pathname: string): FunnelStep | null {
  const p = pathname.replace(/\/+$/, '').toLowerCase();
  if (/^\/vsl(-a|-b)?$/.test(p)) return 'vsl';
  if (p === '/book') return 'book';
  if (p === '/filled') return 'filled';
  return null;
}

const STEP_EVENT: Record<FunnelStep, string> = { vsl: 'vsl_view', book: 'book_view', filled: 'call_booked' };

/** Load the Clarity tag once (no-op if GTM or a previous call already injected it). */
export function ensureClarityTag(d: Document = document) {
  if (d.querySelector(`script[src*="clarity.ms/tag/${CLARITY_PROJECT_ID}"]`)) return;
  const w = window as unknown as { clarity?: { (...a: unknown[]): void; q?: IArguments[] } };
  w.clarity = w.clarity || function () { (w.clarity!.q = w.clarity!.q || []).push(arguments); };
  const s = d.createElement('script');
  s.async = true;
  s.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  d.head.appendChild(s);
}

/** Sends the funnel tags + step event for the current page. Returns the calls made. */
export function reportFunnelStep(step: FunnelStep, search: string, assigned?: string | null) {
  const params = new URLSearchParams(search);
  const calls: unknown[][] = [['set', 'funnel_step', step]];
  const variation = params.get('landing_page_variation') || assigned || '';
  if (variation === 'A' || variation === 'B') calls.push(['set', 'landing_page_variation', variation]);
  for (const key of ['utm_source', 'utm_campaign', 'utm_content']) {
    const v = params.get(key);
    if (v) calls.push(['set', key, v]);
  }
  calls.push(['event', STEP_EVENT[step]]);
  calls.forEach((c) => reportVslClarity(...c));
  return calls;
}

export const FunnelClarity = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const step = funnelStep(pathname);
    if (!step) return;
    // /filled inside an iframe is promoted to the top page; only the top page reports.
    if (step === 'filled' && window.self !== window.top) return;
    ensureClarityTag();
    const assigned = document.documentElement.dataset.vslLandingVariation;
    reportFunnelStep(step, window.location.search, assigned);
  }, [pathname, search]);
  return null;
};
